// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { CheckCircle2, ChevronRight, Loader2, MinusCircle, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

export type PipelineCheckStatus = 'pass' | 'fail' | 'skipped' | 'running';

export interface PipelineCheckLogs {
  text: string;
  truncated: boolean;
}

export interface PipelineCheckRowProps {
  name: string;
  status: PipelineCheckStatus;
  /** One-line summary shown under the name (error summary or duration). */
  detail?: React.ReactNode;
  /** Remediation hint, surfaced at the top of the expansion. */
  suggestedFix?: string | null;
  /** Producer-shaped structured failure rows (pipeline_run_checks.error_details). */
  details?: ReadonlyArray<Record<string, unknown>>;
  /** Raw log tail — set only on checks that have one, after a `detail=full` read. */
  logs?: PipelineCheckLogs;
  className?: string;
  'data-testid'?: string;
}

const STATUS_ICON: Record<
  PipelineCheckStatus,
  { Icon: typeof CheckCircle2; iconClass: string; label: string }
> = {
  pass: {
    Icon: CheckCircle2,
    iconClass: 'text-gl-green-text',
    label: 'Pass',
  },
  fail: {
    Icon: XCircle,
    iconClass: 'text-gl-red-text',
    label: 'Fail',
  },
  skipped: {
    Icon: MinusCircle,
    iconClass: 'text-[hsl(var(--text-tertiary))]',
    label: 'Skipped',
  },
  running: {
    Icon: Loader2,
    iconClass: 'text-gl-amber-text motion-safe:animate-spin',
    label: 'Running',
  },
};

// The deploy detail's `logs` array is surfaced through the dedicated logs
// disclosure, so it's never rendered as a key/value line; everything else is
// stringified verbatim (the producer owns the keys — see CheckErrorDetail).
function formatDetailValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

export function PipelineCheckRow({
  name,
  status,
  detail,
  suggestedFix,
  details,
  logs,
  className,
  'data-testid': testId,
}: PipelineCheckRowProps) {
  const { Icon, iconClass, label } = STATUS_ICON[status];
  const structured = (details ?? []).filter((d) => Object.keys(d).some((k) => k !== 'logs'));
  const expandable = Boolean(suggestedFix) || structured.length > 0 || logs !== undefined;
  const [expanded, setExpanded] = React.useState(false);
  const [logsOpen, setLogsOpen] = React.useState(false);
  const regionId = React.useId();

  const header = (
    <>
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', iconClass)} aria-label={label} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-foreground">{name}</div>
        {detail !== undefined && (
          <div className="mt-0.5 text-xs text-muted-foreground">{detail}</div>
        )}
      </div>
      {expandable && (
        <ChevronRight
          className={cn(
            'mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform',
            expanded && 'rotate-90',
          )}
          aria-hidden
        />
      )}
    </>
  );

  if (!expandable) {
    return (
      <div
        data-testid={testId}
        data-status={status}
        className={cn(
          'flex items-start gap-3 border-b border-border px-3 py-2.5 last:border-b-0',
          className,
        )}
      >
        {header}
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      data-status={status}
      className={cn('border-b border-border last:border-b-0', className)}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={regionId}
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-accent/50"
      >
        {header}
      </button>
      {expanded && (
        <div id={regionId} className="space-y-3 px-3 pb-3 pl-10">
          {suggestedFix && (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Suggested fix: </span>
              {suggestedFix}
            </p>
          )}
          {structured.length > 0 && (
            <ul className="space-y-2">
              {structured.map((d, i) => (
                <li key={i} className="rounded-gl border border-border bg-muted/30 p-2">
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
                    {Object.entries(d)
                      .filter(([k]) => k !== 'logs')
                      .map(([k, v]) => (
                        <React.Fragment key={k}>
                          <dt className="font-mono uppercase tracking-[0.06em] text-muted-foreground">
                            {k}
                          </dt>
                          <dd className="min-w-0 break-words text-foreground">
                            {formatDetailValue(v)}
                          </dd>
                        </React.Fragment>
                      ))}
                  </dl>
                </li>
              ))}
            </ul>
          )}
          {logs && (
            <div>
              <button
                type="button"
                aria-expanded={logsOpen}
                onClick={() => setLogsOpen((v) => !v)}
                className="text-xs font-medium text-link hover:underline"
              >
                {logsOpen ? 'Hide logs' : 'View logs'}
              </button>
              {logsOpen && (
                <>
                  <pre className="mt-2 max-h-72 overflow-auto rounded-gl border border-border bg-muted/40 p-3 font-mono text-[11px] leading-5">
                    {logs.text || '(empty)'}
                  </pre>
                  {logs.truncated && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Output truncated to the most recent lines.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
